import {CursorType} from '@/enums/diagrams';
import {CursorData} from '../cursorDiagramState';
import {CursorLabelModel} from './labelModels/cursorLabelModel';
import {SimpleCursor} from './simpleCursor';
import {uuidv4} from '@u/uuidUtils';
import {MarkerSeriesHarmonicPoints} from '@/interfaces/markers';
import {CursorPlotLine} from './cursorPlotLine';
import {CursorManager} from './cursorManager';

/*
  Класс гармонического курсора
*/
export class HarmonicCursor extends SimpleCursor {
  // Тип курсора
  type: CursorType = CursorType.HarmonicCursor;

  // Префикс идентификатора линии гармоники
  private harmonicLineIdPrefix = 'hrm-line-';

  // Количество линий гармоник (не считая основную линию курсора)
  linesCount = 4;

  // Список линий гармоник
  private harmonicCursorLines: CursorPlotLine[] = [];

  // Боковые гармоники
  subLinesCount = 0;
  subLinesFreqShift = 20.0;
  private subHarmonicLines: CursorPlotLine[][] = [];

  // Идентификаторы серий с маркерами гармоник
  private harmonicMarkerSeries: MarkerSeriesHarmonicPoints[] = [];

  // Идентификаторы серий с маркерами гармоник
  private subHarmonicMarkerSeries: MarkerSeriesHarmonicPoints[] = [];

  // Функция получения гармоник
  private harmonicFunction: Function;
  private antiHarmonicFunction: Function;

  // Цвет маркеров и линий для гармоник
  private harmonicColor: string;

  // Список отметок для гармоник
  private harmonicTooltips: any[] = [];

  constructor(
    labelModel: CursorLabelModel | null = null,
    applyCallback: Function | null = null,
    color = '#00FF00FF', // 'rgba(0, 255, 0, 1)',
    linesCount = 4,
    harmonicFunction: Function = (value: number, index: number) => value * (index + 2),
    antiHarmonicFunction: Function = (value: number, index: number) => value / (index + 2),
  ) {
    super(labelModel, applyCallback, color);
    (this.linesCount = linesCount),
      (this.harmonicColor =
        '#' +
        Math.trunc(parseInt(color.slice(1, 3), 16) * 0.5)
          .toString(16)
          .padStart(2, '0') +
        Math.trunc(parseInt(color.slice(3, 5), 16) * 0.5)
          .toString(16)
          .padStart(2, '0') +
        Math.trunc(parseInt(color.slice(5, 7), 16) * 0.5)
          .toString(16)
          .padStart(2, '0') +
        color.slice(7, 9));
    this.harmonicFunction = harmonicFunction;
    this.antiHarmonicFunction = antiHarmonicFunction;
  }

  /*
    Получить данные курсора
  */
  getCursorData() {
    return new CursorData(
      this.id,
      this.type,
      this.copiedPoint?.x,
      this.linesCount,
      this.subLinesCount,
      this.subLinesFreqShift,
    );
  }

  /*
    Инициализация курсора
  */
  init(
    cursorManager: CursorManager | null,
    chart: any,
    bindedSeriesIds: string[] = [],
    isShowLine = true,
    isShowTooltips = false,
    cursorData: CursorData | null | undefined,
  ) {
    this.linesCount =
      typeof cursorData?.linesCount === 'number' ? cursorData?.linesCount : this.linesCount;
    this.subLinesCount =
      typeof cursorData?.subLinesCount === 'number'
        ? cursorData?.subLinesCount
        : this.subLinesCount;
    this.subLinesFreqShift =
      typeof cursorData?.subLinesFreqShift === 'number' ? cursorData?.subLinesFreqShift : 20.0;
    this.harmonicColor =
      '#' +
      Math.trunc(parseInt(this.color.slice(1, 3), 16) * 0.5)
        .toString(16)
        .padStart(2, '0') +
      Math.trunc(parseInt(this.color.slice(3, 5), 16) * 0.5)
        .toString(16)
        .padStart(2, '0') +
      Math.trunc(parseInt(this.color.slice(5, 7), 16) * 0.5)
        .toString(16)
        .padStart(2, '0') +
      this.color.slice(7, 9);
    this.initHarmonicLines(chart);
    super.init(cursorManager, chart, bindedSeriesIds, isShowLine, isShowTooltips, cursorData);
  }

  /*
    Обновление курсора
  */
  update(
    isShowLine = true,
    isShowTooltips = false,
    cursorData: CursorData | null | undefined,
    bindedSeriesIds: string[],
  ) {
    super.update(isShowLine, isShowTooltips, cursorData, bindedSeriesIds);
    this.redrawHarmonics();
  }

  /*
    Инициализация данных курсора
  */
  protected initCursorData(cursorData: CursorData | null | undefined) {
    this.linesCount =
      typeof cursorData?.linesCount === 'number' ? cursorData?.linesCount : this.linesCount;
    this.subLinesCount =
      typeof cursorData?.subLinesCount === 'number'
        ? cursorData?.subLinesCount
        : this.subLinesCount;
    this.subLinesFreqShift =
      typeof cursorData?.subLinesFreqShift === 'number'
        ? cursorData?.subLinesFreqShift
        : this.subLinesFreqShift;
    this.redrawHarmonics();
    super.initCursorData(cursorData);
  }

  private initHarmonicLines(chart: any) {
    // инициализируем линии гармоник
    for (let index = 0; index <= this.linesCount; index++) {
      if (index < this.linesCount)
        this.harmonicCursorLines.push(
          new CursorPlotLine(
            chart,
            {
              id: this.getModulationLineId(index),
              value: undefined,
              width: 3,
              color: this.harmonicColor,
              zIndex: 9998,
            },
            true,
            index,
          ),
        );
      this.subHarmonicLines.push([]);
      for (let i = 0; i < this.subLinesCount * 2; i++) {
        this.subHarmonicLines[index].push(
          new CursorPlotLine(
            chart,
            {
              id: `${this.getModulationLineId(index)}-${i}`,
              value: undefined,
              width: 1,
              color: index < this.linesCount ? this.harmonicColor : this.color,
              zIndex: 9998,
            },
            true,
          ),
        );
      }
    }
  }

  /*
    Получить идентификатор линии гармоники
  */
  private getModulationLineId(index: number) {
    return `${this.harmonicLineIdPrefix}-${index}-${this.id}`;
  }

  /*
    Действия при активации курсора
  */
  apply(point: any) {
    super.apply(point);
    this.redrawHarmonics();
  }

  /*
    Рисуем серию с маркерами
  */
  protected drawMarkersHarmonicSeries(
    markerSeriesId: string,
    axisNumber: number,
    markerPoints: any[],
    subharmonic = false,
  ) {
    let markerSeries = this.chart.get(markerSeriesId);

    if (!markerSeries) {
      // если серия с маркерами не найдена, то создаем ее (без перерисовки)
      markerSeries = this.chart.addSeries(
        {
          id: markerSeriesId,
          showInLegend: false,
          type: 'scatter',
          data: [],
          lineWidth: 0,
          zIndex: 9999,
          marker: {
            symbol: 'circle',
            fillColor: subharmonic ? 'rgba(255,255,255,0.5)' : this.harmonicColor,
            lineColor: this.harmonicColor,
            lineWidth: subharmonic ? 1 : 0,
            radius: subharmonic ? 3 : 5,
            enabled: true,
            zIndex: 9999,
          },
          hover: {
            enabled: false,
            halo: {
              size: 0,
            },
            inactive: {
              enabled: false,
            },
          },
          states: {
            inactive: {
              opacity: 1,
            },
          },
          yAxis: axisNumber,
        },
        false,
      );
    }

    // добавляем точки маркеров в серию (с перерисовкой)
    // нужно обязательно сортировать данные перед добавлением в серию
    markerPoints.sort((a: any, b: any) => a.x - b.x);

    markerSeries.setData(markerPoints);
  }

  protected newHarmonicSeriesId(id: string) {
    return `harmonicMarkerSeries-${id}`;
  }

  protected onUpHandler() {
    super.onUpHandler();
    this.harmonicCursorLines.filter(l => l.isDrag()).forEach(l => l.stopDrag());
    this.subHarmonicLines.forEach(arr => arr.filter(l => l.isDrag()).forEach(l => l.stopDrag()));
  }

  /*
    Действия при перемещении
  */
  protected onMoveHandler(e: any) {
    let redraw_flag = false;
    super.onMoveHandler(e);
    this.harmonicCursorLines.forEach((line: CursorPlotLine) => {
      if (line.isDrag()) {
        const newXValue = line.getTranslatedXValue(e.pageX);
        line.translateX(e.pageX, e.chartX);
        this.copiedPoint = {x: this.antiHarmonicFunction(newXValue, line.index), y: 0};
        this.applyWithNotification({x: this.copiedPoint.x, y: this.copiedPoint.y});
      }
    });
    if (this.copiedPoint)
      this.subHarmonicLines.forEach((arr: CursorPlotLine[], index: number) => {
        const shiftedValue =
          index == this.linesCount
            ? this.copiedPoint?.x
            : this.harmonicFunction(this.copiedPoint?.x, index);
        arr.forEach((l: CursorPlotLine, i: number) => {
          if (!l.isDrag()) return;
          const newXValue = l.getTranslatedXValue(e.pageX);
          this.subLinesFreqShift = Math.max(
            (1.0 * (newXValue - shiftedValue)) /
              (i < this.subLinesCount ? i - this.subLinesCount : i + 1 - this.subLinesCount),
            0.1,
          );
          redraw_flag = true;
        });
      });
    if (this.cursorPlotLine.isDrag() || redraw_flag) {
      // Переносим все гармонические линии
      this.redrawHarmonics();
      this.notify();
    }
  }

  /*
    Перерисовать линии гармоник и (если нужно) серию с маркерами
  */
  private redrawHarmonics(redrawMarkers = true) {
    if (this.copiedPoint) {
      const extremes = this.chart.xAxis[0].getExtremes();

      const value = this.copiedPoint.x;

      const harmonicSeries: MarkerSeriesHarmonicPoints[] = [];
      const subHarmonicSeries: MarkerSeriesHarmonicPoints[] = [];
      // для всех сравниваемых графиков находим точки пересечения с курсором и запоминаем их, вместе с номерами соответствующих осей
      this.bindedSeries.forEach((series: any) => {
        const s = this.chart.series.find((s: any) => s.userOptions.id == series.options.id);
        if (!s.visible) return;
        const harmonicPointsChart = this.redrawHarmonicLines(value, extremes, series);

        harmonicSeries.push({
          chartSeriesId: series.options.id,
          yAxis: series.options.yAxis,
          points: harmonicPointsChart[0],
        });
        subHarmonicSeries.push({
          chartSeriesId: series.options.id,
          yAxis: series.options.yAxis,
          points: harmonicPointsChart[1],
        });
      });

      if (!redrawMarkers) return;

      this.harmonicMarkerSeries.forEach((series: any) => {
        const findIndex = harmonicSeries.findIndex(
          (newSeries: any) => newSeries.chartSeriesId === series.chartSeriesId,
        );
        if (findIndex === -1) this.chart.get(series.id)?.remove();
        else harmonicSeries[findIndex] = {...harmonicSeries[findIndex], id: series.id};
      });

      this.harmonicMarkerSeries = [...harmonicSeries];
      this.harmonicMarkerSeries.forEach((series: any) => {
        series.id = series.id !== undefined ? series.id : this.newHarmonicSeriesId(uuidv4());
        this.drawMarkersHarmonicSeries(series.id, series.yAxis, series.points);
      });

      this.subHarmonicMarkerSeries.forEach((series: any) => {
        const findIndex = subHarmonicSeries.findIndex(
          (newSeries: any) => newSeries.chartSeriesId === series.chartSeriesId,
        );
        if (findIndex === -1) this.chart.get(series.id)?.remove();
        else subHarmonicSeries[findIndex] = {...subHarmonicSeries[findIndex], id: series.id};
      });

      this.subHarmonicMarkerSeries = [...subHarmonicSeries];
      this.subHarmonicMarkerSeries.forEach((series: any) => {
        series.id = series.id !== undefined ? series.id : this.newHarmonicSeriesId(uuidv4());
        this.drawMarkersHarmonicSeries(series.id, series.yAxis, series.points, true);
      });
    }
  }

  /*
    Логика перерисовки гармонических линий (отдельный метод для сокращения похожего кода)
  */
  private redrawHarmonicLines(value: number, extremes: any, series: any) {
    if (
      this.linesCount != this.harmonicCursorLines.length ||
      this.subHarmonicLines.length != this.linesCount + 1 ||
      !this.subHarmonicLines.reduce((res, arr) => {
        return arr.length == this.subLinesCount * 2;
      }, true)
    ) {
      this.clear();
      this.init(
        this.cursorManager,
        this.chart ?? this.cursorManager?.getChart(),
        this.cursorManager?.bindedSeriesIds,
        this.cursorManager?.isShowLine,
        this.cursorManager?.isShowTooltips,
        this.getCursorData(),
      );
    }
    const harmonicPoints: any[] = [];
    const subHarmonicPoints: any[] = [];
    this.harmonicCursorLines.forEach((line: CursorPlotLine, index: number) => {
      const shiftedValue = this.harmonicFunction(value, index);
      const isInExtreme = extremes.min <= shiftedValue && extremes.max >= shiftedValue;
      if (!line.isDrag()) {
        //        line.remove();
      }
      if (isInExtreme) {
        const intersectionPoint = this.getIntersectionPoint(shiftedValue, series);
        if (intersectionPoint) {
          harmonicPoints.push({
            x: shiftedValue,
            y: intersectionPoint.y,
          });
        }
      }
      if (this.isShowLine && !line.isDrag()) {
        line.redraw(shiftedValue);
      }
      this.subHarmonicLines[index].forEach((l: CursorPlotLine, i: number) => {
        const val =
          shiftedValue +
          this.subLinesFreqShift *
            (i < this.subLinesCount ? i - this.subLinesCount : i + 1 - this.subLinesCount);
        l.redraw(val);

        const intersectionPoint = this.getIntersectionPoint(val, series);
        if (intersectionPoint) {
          subHarmonicPoints.push({
            x: val,
            y: intersectionPoint.y,
          });
        }
      });
    });
    this.subHarmonicLines[this.linesCount].forEach((l: CursorPlotLine, i: number) => {
      const val =
        value +
        this.subLinesFreqShift *
          (i < this.subLinesCount ? i - this.subLinesCount : i + 1 - this.subLinesCount);
      l.redraw(val);

      const intersectionPoint = this.getIntersectionPoint(val, series);
      if (intersectionPoint) {
        subHarmonicPoints.push({
          x: val,
          y: intersectionPoint.y,
        });
      }
    });
    return [harmonicPoints, subHarmonicPoints];
  }

  /*
    Действия при очистке курсора
  */
  clear() {
    this.clearHarmonicTooltips();
    this.harmonicCursorLines.forEach((line: CursorPlotLine) => {
      line.remove();
    });
    this.harmonicCursorLines = [];
    this.subHarmonicLines.forEach(a => a.forEach((l: CursorPlotLine) => l.remove()));
    this.subHarmonicLines = [];

    // удаляем с графика серию с маркерами
    this.harmonicMarkerSeries.forEach((series: any) => {
      this.chart.get(series.id)?.remove();
    });

    this.harmonicMarkerSeries = [];
    super.clear();
  }

  /*
    Действия при перерисовке (любом изменении графика)
  */
  render() {
    super.render();

    // если из сравнения был удален график, а с ним и ось, то нужно убрать соответствующую серию маркеров
    this.harmonicMarkerSeries = this.harmonicMarkerSeries.filter((series: any) => {
      return this.chart.yAxis[series.yAxis] !== undefined;
    });

    this.redrawHarmonicTooltips();
  }

  /*
    Перерисовать отметки гармоник
  */
  private redrawHarmonicTooltips() {
    this.clearHarmonicTooltips();
    if (!this.isShowTooltips) return;

    this.harmonicMarkerSeries.forEach((series: any) => {
      series.points.forEach((p: any) => {
        const t = this.drawTooltip({x: p.x, y: p.y}, series.yAxis);
        this.harmonicTooltips.push(t);
      });
    });

    this.subHarmonicMarkerSeries.forEach((series: any) => {
      series.points.forEach((p: any) => {
        const t = this.drawTooltip({x: p.x, y: p.y}, series.yAxis);
        this.harmonicTooltips.push(t);
      });
    });
  }

  /*
    Очистить отметки гармоник
  */
  private clearHarmonicTooltips() {
    this.harmonicTooltips.forEach((tooltip: any) => tooltip.destroy());
    this.harmonicTooltips = [];
  }

  /*
    Переключение показа линии
  */
  showLine(show: boolean) {
    super.showLine(show);
    this.redrawHarmonics(false);
  }

  /*
    Переключение показа отметок
  */
  showTooltips(show: boolean) {
    super.showTooltips(show);
    this.redrawHarmonicTooltips();
  }

  public getLabelStr() {
    let labelStr: string = super.getLabelStr().trim();

    if (this.subLinesCount > 0 && this.subLinesFreqShift > 0) {
      if (this.labelModel && this.labelModel.xUnits !== '' && this.labelModel.xUnits != '-')
        labelStr += `, Полоса, ${this.labelModel.xUnits}: ${this.subLinesFreqShift.toFixed(2)}`;
      else if (this.labelModel && this.labelModel.xUnits == '-')
        labelStr += `, Полоса: ${this.subLinesFreqShift.toFixed(2)}`;
      else labelStr += `, Полоса: ${this.subLinesFreqShift.toFixed(2)}`;
    }
    return labelStr;
  }
}
